import { ValueType } from 'interfaces/valueType.interface'

import {
  CandidateRankingModel,
  MediaModel,
  PresentationsModel,
  PressReleasesModel,
} from 'config/common/baseModel'
import { DiseaseInformationModel } from 'config/common/baseModel/biorisk/DiseaseInformation'
import { OutbreakTrackerModel } from 'config/common/baseModel/biorisk/OutbreakTracker'
import { customFilterNaNGraphData } from 'config/common/transformValue/charts'

import {
  addOrOverrideColumn,
  compose,
  renameColumn,
  updateColumnAttributes,
  whiteListColumn,
} from 'utils/overrideModel'
import { IDACandidatesDetailView } from 'routes/apps/ida360/Candidates'
import { ApprovalsModel } from 'config/common/baseModel/Approvals'
import { SortOrders } from 'enums/SortOrders'

export const VariantsCandidateRankingMutationsModel = compose<
  typeof CandidateRankingModel
>(
  renameColumn('countryFirstDetected', 'Country First Detected'),
  whiteListColumn<typeof CandidateRankingModel>([
    'linkAfTrUniqueTreatmentNameToBnf',
    'afSynonymousMutations',
    'presentations',
    'pressReleasesByTitle',
    'mediaByTitle',
    'lookupAfTrOtherNamesFromBnf',
    'linkClinicalTrials',
    'afImplications',
    'afImpactVaccineEfficacy',
    'afImpactTreatmentEfficacy',
    'keywordList',
    'afImpactVaccineSera',
  ])
)({
  ...CandidateRankingModel,
  detailViewType: 'Generic',
  name: 'Variants',
})

export const VariantsPressReleasesModel = compose<typeof PressReleasesModel>(
  addOrOverrideColumn<typeof PressReleasesModel>(
    {
      key: 'afUniqueTreatmentName',
      type: ValueType.MULTI,
      label: 'Name',
      width: 200,
      graphType: 'Pie',
      customDataFilter: (data) =>
        customFilterNaNGraphData(data, 'afUniqueTreatmentName'),
    },
    5
  ),
  whiteListColumn([
    'title',
    'links',
    'datePublished',
    'description',
    'afUniqueTreatmentName',
    'companies',
    'afTopic',
    'language',
    'afTags',
  ])
)(PressReleasesModel)

export const BioRiskPressReleasesModel = compose<typeof PressReleasesModel>(
  whiteListColumn([
    'title',
    'description',
    'datePublished',
    'infectiousDisease',
    'grouping2',
    'companies',
    'author',
    'feedOrganisationName',
    'link',
    'afTags',
    'language',
  ])
)(PressReleasesModel)

export const BioriskCandidateModel = compose<typeof CandidateRankingModel>(
  whiteListColumn([
    'candidateName',
    'latestPhaseManual',
    'sponsors',
    'designationB',
    'routeOfAdministration',
    'type',
    'subType',
    'designationA',
    'diseaseName',
    'vaccineValency',
    'targetViralStrain',
  ])
)({
  ...CandidateRankingModel,
  hideViewSelector: true,
  excludeGeneratedCharts: true,
  detailViewType: 'Generic',
  renderDetailView: IDACandidatesDetailView,
})

export const BioriskMpoxCandidateModel = compose<typeof CandidateRankingModel>(
  whiteListColumn([
    'candidateName',
    'latestPhaseManual',
    'sponsors',
    'alternativeNames',
    'diseaseName',
    'pathogenTypes',
    'designationB',
    'routeOfAdministration',
    'type',
    'subType',
    'vaccineAdjuvants',
    'targetPopulation',
    'vaccineValency',
    'targetViralStrain',
    'deliverySystemName',
    'frontendNotes',
  ]),
  updateColumnAttributes('latestPhaseManual', {
    sortOrderObject: [
      'Approved',
      'Phase III',
      'Phase II/III',
      'Phase II',
      'Phase I/II',
      'Phase I',
      'Preclinical',
    ],
  })
)({
  ...CandidateRankingModel,
  defaultSortObject: [
    {
      id: 'latestPhaseManual',
      sortOrder: SortOrders.ASC,
    },
  ],
})

export const VariantsMediaModel = compose<typeof MediaModel>(
  addOrOverrideColumn<typeof MediaModel>(
    {
      key: 'afUniqueTreatmentName',
      type: ValueType.MULTI,
      label: 'Name',
      width: 200,
      graphType: 'Pie',
      customDataFilter: (data) =>
        customFilterNaNGraphData(data, 'afUniqueTreatmentName'),
    },
    5
  ),
  addOrOverrideColumn<typeof MediaModel>(
    {
      key: 'author',
      type: ValueType.MULTI,
      label: 'Authors',
      width: 200,
      customDataFilter: (data) => {
        return data?.filter((dataItem) => dataItem.author !== null)
      },
    },
    6
  ),
  whiteListColumn([
    'title',
    'mediaProvider',
    'datePublished',
    'description',
    'link',
    'language',
    'author',
    'companyResearchInstitution',
    'afTopic',
    'afTags',
    'afUniqueTreatmentName',
  ])
)(MediaModel)

export const BioRiskMediaModel = compose<typeof MediaModel>(
  whiteListColumn([
    'title',
    'description',
    'datePublished',
    'infectiousDisease',
    'grouping2',
    'link',
    'companies',
    'afSentimentAnalysis',
    'author',
    'afTags',
    'language',
  ])
)(MediaModel)

export const TreatmentOtherPapersModel = whiteListColumn<
  typeof PresentationsModel
>([
  'title',
  'journals',
  'channel',
  'publicationDate',
  'articleLink',
  'candidates',
  'abstract',
  'reviewStatus',
  'articleType',
  'journalImpactScore',
  'afRiskFactor',
])({
  ...PresentationsModel,
  name: 'Papers',
})
export const OutbreakOverviewTrackerModel = whiteListColumn<
  typeof OutbreakTrackerModel
>([
  'diseaseName',
  'continent',
  'country',
  'area',
  'valueNewInfections',
  'dateEndNewInfections',
  'sourceNewInfections',
  'valueNewDeaths',
  'dateEndNewDeaths',
  'sourceNewDeaths',
  'diseaseRi',
  'diseasePathogenFamily',
  'diseasePathogenType',
  'diseaseCategory',
  'diseaseModeOfTransmission',
])({
  ...OutbreakTrackerModel,
  name: 'Outbreak Tracker',
})

export const BioriskDiseaseInformationModel = compose<
  typeof DiseaseInformationModel
>(
  whiteListColumn([
    'afDiseaseName',
    'pathogenType',
    'pathogenFamily',
    'afModeOfTransmission',
    'rawCaseFatalityPercentage',
    'transmissionR0Average',
    'pathogenPriority',
    'characteristics',
    'afDiseaseRi',
  ]),
  updateColumnAttributes('characteristics', {
    type: ValueType.LONG,
    width: 500,
  })
)(DiseaseInformationModel)

export const LongCovidPapersModel = compose<typeof PresentationsModel>(
  renameColumn('channel', 'Article Type'),
  updateColumnAttributes('title', {
    type: ValueType.LONG,
  }),
  whiteListColumn(['title', 'journals', 'abstract', 'channel', 'articleLink'])
)({ ...PresentationsModel, name: 'Publications' })

export const BioriskApprovalsModel = compose<typeof ApprovalsModel>(
  whiteListColumn([
    'candidateName',
    'sponsors',
    'type',
    'area',
    'regulatoryOrganisation',
    'targetPopulations',
    'approvalDate',
    'associatedDocumentUrl',
    'designationA',
    'internalNotes',
  ])
)({ ...ApprovalsModel, name: 'Mpox New Approvals' })
